import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import api from '../services/api';
import { BoltIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // PWA Install Prompt State
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gymParam = searchParams.get('gym');

  // ---------------------------------------------------------
  // Basic page initialization
  // ---------------------------------------------------------
  useEffect(() => {
    setEmail('');
    setPassword('');
    setError('');

    document.documentElement.style.setProperty(
      '--accent-color',
      '#f97316'
    );
  }, []);

  // ---------------------------------------------------------
  // PWA detection + installation handling
  // ---------------------------------------------------------
  useEffect(() => {
    // Detect whether the app is already installed/running standalone.
    const checkStandalone = () => {
      return (
        window.matchMedia &&
        window.matchMedia('(display-mode: standalone)').matches
      ) || window.navigator.standalone === true;
    };

    const isStandalone = checkStandalone();

    // -------------------------------------------------------
    // Detect iPhone / iPad / iPod
    // Also supports modern iPadOS devices reporting MacIntel.
    // -------------------------------------------------------
    const userAgent = window.navigator.userAgent || '';

    const ios =
      /iPhone|iPad|iPod/i.test(userAgent) ||
      (
        window.navigator.platform === 'MacIntel' &&
        window.navigator.maxTouchPoints > 1
      );

    setIsIOS(ios);

    // If already installed, never show the install banner.
    if (isStandalone) {
      setShowInstallPrompt(false);
      return undefined;
    }

    // -------------------------------------------------------
    // iOS
    //
    // iOS does not expose beforeinstallprompt.
    // Therefore we show the same custom banner, but cannot
    // programmatically force Apple's Add to Home Screen dialog.
    // -------------------------------------------------------
    if (ios) {
      const dismissed =
        localStorage.getItem('pwa_ios_prompt_dismissed');

      if (!dismissed) {
        const timer = setTimeout(() => {
          setShowInstallPrompt(true);
        }, 1500);

        return () => clearTimeout(timer);
      }

      return undefined;
    }

    // -------------------------------------------------------
    // Android / Desktop supported browsers
    // -------------------------------------------------------
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();

      setDeferredPrompt(event);

      const dismissed = sessionStorage.getItem(
        'pwa_prompt_dismissed'
      );

      if (!dismissed && !checkStandalone()) {
        setShowInstallPrompt(true);
      }
    };

    window.addEventListener(
      'beforeinstallprompt',
      handleBeforeInstallPrompt
    );

    // -------------------------------------------------------
    // Detect installation after the browser completes install.
    // -------------------------------------------------------
    const handleAppInstalled = () => {
      console.log('PWA installed successfully.');

      setDeferredPrompt(null);
      setShowInstallPrompt(false);

      sessionStorage.removeItem('pwa_prompt_dismissed');
      localStorage.removeItem('pwa_ios_prompt_dismissed');
    };

    window.addEventListener(
      'appinstalled',
      handleAppInstalled
    );

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );

      window.removeEventListener(
        'appinstalled',
        handleAppInstalled
      );
    };
  }, []);

  // ---------------------------------------------------------
  // Install button
  // ---------------------------------------------------------
  const handleInstallClick = async () => {
    // -------------------------------------------------------
    // iOS
    // -------------------------------------------------------
    if (isIOS) {
      /*
       * IMPORTANT:
       * iOS does not provide a JavaScript API that directly
       * opens "Add to Home Screen".
       *
       * Web Share is the closest supported native interaction.
       * It opens the iOS native share sheet when available.
       *
       * The website cannot automatically select
       * "Add to Home Screen".
       */
      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function'
      ) {
        try {
          await navigator.share({
            title: 'SmartGoGym',
            text: 'Install SmartGoGym App',
            url: window.location.href,
          });

          return;
        } catch (error) {
          // User closed the native share sheet.
          // Do not show an instruction modal.
          if (error?.name !== 'AbortError') {
            console.error(
              'iOS share sheet failed:',
              error
            );
          }

          return;
        }
      }

      // No supported native share API.
      // Do not fake an installation or show instructions.
      console.warn(
        'iOS Web Share API is not available in this browser.'
      );

      return;
    }

    // -------------------------------------------------------
    // Android / Desktop
    // -------------------------------------------------------
    if (!deferredPrompt) {
      console.warn(
        'Native PWA installation prompt is not available.'
      );

      return;
    }

    try {
      // Open the browser's REAL native PWA install prompt.
      deferredPrompt.prompt();

      const { outcome } =
        await deferredPrompt.userChoice;

      console.log(
        `PWA installation outcome: ${outcome}`
      );

      // The beforeinstallprompt event can only be used once.
      setDeferredPrompt(null);

      if (outcome === 'accepted') {
        setShowInstallPrompt(false);
      }
    } catch (error) {
      console.error(
        'PWA installation failed:',
        error
      );
    }
  };

  // ---------------------------------------------------------
  // Close install banner
  // ---------------------------------------------------------
  const handleCancelClick = () => {
    setShowInstallPrompt(false);

    if (isIOS) {
      localStorage.setItem(
        'pwa_ios_prompt_dismissed',
        'true'
      );
    } else {
      sessionStorage.setItem(
        'pwa_prompt_dismissed',
        'true'
      );
    }
  };

  // ---------------------------------------------------------
  // QR Attendance
  // ---------------------------------------------------------
  const markAttendance = async (qrCode) => {
    setMarkingAttendance(true);

    try {
      // Send the full URL since QR now encodes a URL
      const frontendUrl = window.location.origin;
      const fullQrUrl = `${frontendUrl}/login?gym=${qrCode}`;

      const response = await api.post(
        '/api/attendance/qr-checkin',
        {
          qr_code: fullQrUrl,
        }
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (err) {
      console.error(
        'Attendance marking failed:',
        err
      );

      return {
        success: false,
        error:
          err.response?.data?.error ||
          'Failed to mark attendance',
      };
    } finally {
      setMarkingAttendance(false);
    }
  };

  // ---------------------------------------------------------
  // Login
  // ---------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please fill in all fields.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await api.post(
        '/api/auth/login',
        {
          email,
          password,
        }
      );

      const { token, user } = response.data;

      // Store authentication data
      login(token, user);

      // -----------------------------------------------------
      // Role-based redirection
      // -----------------------------------------------------
      if (user.role === 'super_admin') {
        navigate('/admin/dashboard');
      }

      else if (user.role === 'gym_owner') {
        navigate('/dashboard');
      }

      else if (user.role === 'member') {
        // Mark attendance automatically if gym param exists
        if (gymParam) {
          const attendanceResult =
            await markAttendance(gymParam);

          if (attendanceResult.success) {
            navigate('/member/dashboard', {
              state: {
                attendanceSuccess: true,
                attendanceData:
                  attendanceResult.data.attendance,
              },
            });
          }

          else {
            navigate('/member/dashboard', {
              state: {
                attendanceError:
                  attendanceResult.error,
              },
            });
          }
        }

        else {
          navigate('/member/dashboard');
        }
      }

      else {
        // Fallback for unknown roles
        navigate('/dashboard');
      }
    }

    catch (err) {
      console.error(err);

      // -----------------------------------------------------
      // Server response error
      // -----------------------------------------------------
      if (err.response) {
        const status = err.response.status;
        const serverError =
          err.response.data?.error;

        if (status === 401) {
          setError(
            'Invalid email or password'
          );
        }

        else if (status === 403) {
          setError(
            serverError ||
            'Account is disabled or suspended. Please contact support.'
          );
        }

        else if (status === 404) {
          setError(
            'Account not found'
          );
        }

        else if (status === 500) {
          setError(
            'Server error. Please try again later.'
          );
        }

        else {
          setError(
            serverError ||
            'Login failed. Please try again.'
          );
        }
      }

      // -----------------------------------------------------
      // Network error
      // -----------------------------------------------------
      else if (err.request) {
        setError(
          'Failed to connect. Please ensure your backend server and MySQL database are running.'
        );
      }

      // -----------------------------------------------------
      // Unknown error
      // -----------------------------------------------------
      else {
        setError(
          'An unexpected error occurred. Please try again.'
        );
      }
    }

    finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // UI
  // ---------------------------------------------------------
  return (
    <>
      <SEO
        title="Gym Owner Login - SmartGoNext Gym Management Software Worldwide"
        description="Log in to SmartGoNext Gym — the best gym management software worldwide. Access your gym owner dashboard to manage memberships, biometric/RFID/QR attendance, Stripe/PayPal/UPI billing, payments, and more. Secure login for gym owners, super admins, and members. Used globally in USA, UK, Canada, Australia, Europe, Asia, Middle East, and Africa."
        canonicalUrl="https://gym.smartgonext.com/login"
        type="website"
        noIndex={false}
      />

      <div className="bg-white min-h-screen flex flex-col relative overflow-hidden">

        {/* =====================================================
            PWA INSTALL BANNER
            ===================================================== */}
        {showInstallPrompt && (
          <div
            className="
              fixed
              bottom-6
              left-1/2
              -translate-x-1/2
              z-50
              w-[calc(100%-2rem)]
              max-w-md
              bg-slate-950/95
              border
              border-slate-800
              text-white
              rounded-2xl
              shadow-[0_20px_50px_rgba(0,0,0,0.5)]
              p-5
              backdrop-blur-md
              animate-pwa-slide-up
            "
          >
            <div className="flex gap-4 items-start">

              {/* Icon */}
              <div
                className="
                  bg-orange-500/10
                  p-3
                  rounded-xl
                  border
                  border-orange-500/20
                  text-orange-500
                  shrink-0
                "
              >
                <BoltIcon
                  className="w-6 h-6 animate-pulse"
                />
              </div>

              {/* Content */}
              <div className="flex-1">

                <h3
                  className="
                    text-sm
                    font-bold
                    text-white
                    flex
                    items-center
                    gap-1.5
                  "
                >
                  Install SmartGoGym App

                  <span
                    className="
                      text-[10px]
                      bg-orange-500/20
                      text-orange-400
                      px-1.5
                      py-0.5
                      rounded-full
                      font-semibold
                    "
                  >
                    PWA
                  </span>
                </h3>

                <p
                  className="
                    text-xs
                    text-slate-400
                    mt-1.5
                    leading-relaxed
                  "
                >
                  Add this app to your home screen
                  for quick, one-tap access and a
                  native app experience.
                </p>

                {/* Buttons */}
                <div
                  className="
                    flex
                    items-center
                    justify-end
                    gap-3
                    mt-4
                  "
                >
                  <button
                    type="button"
                    onClick={handleCancelClick}
                    className="
                      text-slate-400
                      hover:text-slate-200
                      text-xs
                      font-semibold
                      px-3
                      py-2
                      rounded-lg
                      transition-colors
                      focus:outline-none
                    "
                  >
                    Not Now
                  </button>

                  <button
                    type="button"
                    onClick={handleInstallClick}
                    className="
                      bg-orange-500
                      hover:bg-orange-600
                      text-white
                      text-xs
                      font-bold
                      px-4
                      py-2
                      rounded-lg
                      shadow-lg
                      shadow-orange-500/15
                      transition-colors
                      focus:outline-none
                    "
                  >
                    Install Now
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* =====================================================
            BACKGROUND
            ===================================================== */}
        <div
          className="
            absolute
            top-1/2
            left-1/2
            -translate-x-1/2
            -translate-y-1/2
            w-[400px]
            h-[400px]
            bg-orange-100/30
            rounded-full
            blur-[100px]
            pointer-events-none
          "
        />

        {/* =====================================================
            MAIN CONTENT
            ===================================================== */}
        <div
          className="
            flex-1
            flex
            items-center
            justify-center
            px-4
            relative
            z-10
          "
        >
          <div className="w-full max-w-md">

            {/* Brand */}
            <div className="text-center mb-8">

              <Link
                to="/"
                className="
                  inline-flex
                  items-center
                  gap-2
                  text-3xl
                  font-extrabold
                  text-gray-900
                "
              >
                <BoltIcon
                  className="
                    w-10
                    h-10
                    text-orange-500
                  "
                />

                <span className="text-orange-500">
                  SmartGoNext Gym
                </span>
              </Link>

              <p
                className="
                  text-gray-600
                  mt-2
                  text-sm
                "
              >
                Log in to manage your gym tenant
                workspace
              </p>

            </div>

            {/* Login Card */}
            <div
              className="
                bg-white
                border
                border-gray-200
                p-8
                rounded-2xl
                shadow-xl
              "
            >

              <h2
                className="
                  text-xl
                  font-bold
                  mb-6
                  text-gray-900
                  text-center
                "
              >
                Welcome Back
              </h2>

              {/* Error */}
              {error && (
                <div
                  className="
                    bg-red-50
                    border
                    border-red-200
                    text-red-600
                    text-xs
                    p-4
                    rounded-xl
                    mb-6
                  "
                >
                  <span className="font-bold">
                    Error:
                  </span>{' '}
                  {error}
                </div>
              )}

              {/* Login Form */}
              <form
                onSubmit={handleSubmit}
                className="space-y-5"
                autoComplete="off"
              >

                {/* Email */}
                <div>

                  <label
                    className="
                      block
                      text-xs
                      font-semibold
                      uppercase
                      tracking-wider
                      text-gray-600
                      mb-2
                    "
                  >
                    Email Address
                  </label>

                  <input
                    type="email"
                    required
                    disabled={loading}
                    value={email}
                    onChange={(e) =>
                      setEmail(e.target.value)
                    }
                    placeholder="Enter your email"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    className="
                      w-full
                      bg-gray-50
                      border
                      border-gray-200
                      focus:border-orange-400
                      focus:bg-white
                      rounded-xl
                      px-4
                      py-3
                      text-sm
                      text-gray-900
                      placeholder-gray-500
                      focus:outline-none
                      transition-all
                      duration-200
                    "
                  />

                </div>

                {/* Password */}
                <div>

                  <div
                    className="
                      flex
                      justify-between
                      items-center
                      mb-2
                    "
                  >
                    <label
                      className="
                        block
                        text-xs
                        font-semibold
                        uppercase
                        tracking-wider
                        text-gray-600
                      "
                    >
                      Password
                    </label>
                  </div>

                  <div className="relative">

                    <input
                      type={
                        showPassword
                          ? 'text'
                          : 'password'
                      }
                      required
                      disabled={loading}
                      value={password}
                      onChange={(e) =>
                        setPassword(e.target.value)
                      }
                      placeholder="Enter your password"
                      autoComplete="off"
                      className="
                        w-full
                        bg-gray-50
                        border
                        border-gray-200
                        focus:border-orange-400
                        focus:bg-white
                        rounded-xl
                        px-4
                        py-3
                        pr-12
                        text-sm
                        text-gray-900
                        placeholder-gray-500
                        focus:outline-none
                        transition-all
                        duration-200
                      "
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowPassword(
                          !showPassword
                        )
                      }
                      disabled={loading}
                      className="
                        absolute
                        right-3
                        top-1/2
                        -translate-y-1/2
                        text-gray-400
                        hover:text-gray-600
                        focus:outline-none
                        disabled:opacity-50
                        disabled:cursor-not-allowed
                        z-10
                      "
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeSlashIcon
                          className="w-5 h-5"
                        />
                      ) : (
                        <EyeIcon
                          className="w-5 h-5"
                        />
                      )}
                    </button>

                  </div>
                </div>

                {/* Sign In */}
                <button
                  type="submit"
                  disabled={loading}
                  className="
                    w-full
                    bg-orange-500
                    hover:bg-orange-600
                    text-white
                    font-bold
                    py-3.5
                    px-4
                    rounded-xl
                    transition-all
                    duration-200
                    shadow-md
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                    text-sm
                    mt-2
                  "
                >
                  {loading ? (
                    <span
                      className="
                        flex
                        items-center
                        justify-center
                        gap-2
                      "
                    >
                      <svg
                        className="
                          animate-spin
                          h-4
                          w-4
                          text-white
                        "
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />

                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="
                            M4 12a8 8 0 018-8V0
                            C5.373 0 0 5.373 0 12h4zm2
                            5.291A7.962 7.962 0 013 7.938l-2.647z
                          "
                        />
                      </svg>

                      Authenticating...
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </button>

              </form>

              {/* Register */}
              <div
                className="
                  mt-8
                  text-center
                  text-xs
                  text-gray-500
                "
              >
                Need a new tenant account?{' '}

                <Link
                  to="/register"
                  className="
                    text-orange-500
                    hover:underline
                    font-semibold
                  "
                >
                  Register Gym & Owner
                </Link>
              </div>

            </div>
          </div>
        </div>

        <Footer />

      </div>
    </>
  );
}